import React, { useEffect, useMemo, useState } from 'react';
import { CapacityComponent } from '@/types';
import { ReusableModal } from '../modals/ReusableModal';
import { CapacityComponentForm } from './CapacityComponentForm';
import {
  useAttachCapacityComponentCriterionLinkMutation,
  useCreateUpdateCapacityComponentMutation,
  useDetachCapacityComponentCriterionLinkMutation,
  useGetCapacityComponentCriterionLinkCandidatesQuery,
  useGetCapacityComponentCriterionLinksQuery,
  useUpdateCapacityComponentCriterionLinksMutation,
} from '../services/edgeApi';
import { useDebouncedState } from '../helpers/hooks';
import toast from 'react-hot-toast';

const EMPTY_LINKS: never[] = [];

export type EditCapacityComponentModalProps = {
  component: CapacityComponent | null;
  onClose: () => void;
  onSave?: () => void | Promise<void>;
};

export const EditCapacityComponentModal: React.FC<
  EditCapacityComponentModalProps
> = ({ component, onClose, onSave: onAfterSave }) => {
  const [updateComponentTrigger] = useCreateUpdateCapacityComponentMutation();
  const [updateCriterionLinksTrigger, { isLoading: isSavingLinks }] =
    useUpdateCapacityComponentCriterionLinksMutation();
  const [attachCriterionLinkTrigger, { isLoading: isAttachingLink }] =
    useAttachCapacityComponentCriterionLinkMutation();
  const [detachCriterionLinkTrigger, { isLoading: isDetachingLink }] =
    useDetachCapacityComponentCriterionLinkMutation();

  const componentId = useMemo(() => {
    if (!component || component.id === 'new') {
      return null;
    }
    const parsed = Number(component.id);
    return Number.isFinite(parsed) ? parsed : null;
  }, [component]);

  const {
    data: criterionLinksData,
    isFetching: isLoadingCriterionLinks,
    refetch: refetchCriterionLinks,
  } = useGetCapacityComponentCriterionLinksQuery(
    {
      componentId: componentId ?? 0,
    },
    {
      skip: componentId === null,
    },
  );
  const criterionLinks = criterionLinksData ?? EMPTY_LINKS;

  const [linkSearchInput, setLinkSearchInput] = useState('');
  const [linkSearch, setLinkSearch] = useDebouncedState('', 300);

  useEffect(() => {
    setLinkSearch(linkSearchInput);
  }, [linkSearchInput, setLinkSearch]);

  const { data: criterionLinkCandidatesData, isFetching: isSearchingLinks } =
    useGetCapacityComponentCriterionLinkCandidatesQuery(
      {
        componentId: componentId ?? 0,
        search: linkSearch,
      },
      {
        skip: componentId === null || linkSearch.trim().length < 2,
      },
    );
  const criterionLinkCandidates = criterionLinkCandidatesData ?? EMPTY_LINKS;

  const onSave = async (updatedComponent: Partial<CapacityComponent>) => {
    const result = await updateComponentTrigger(updatedComponent);
    if ('error' in result) {
      toast.error('Failed to save component');
    } else {
      toast.success(
        `Component #${updatedComponent.id} ${
          component ? 'updated' : 'created'
        } successfully`,
      );
      await onAfterSave?.();
      onClose();
    }
  };

  const onUpdateCriterionLink = async (
    linkId: number,
    updates: {
      creates_flows?: boolean;
      override_direction_sign?: number | null;
    },
  ) => {
    if (componentId === null) {
      return;
    }

    const currentLink = criterionLinks.find((link) => link.id === linkId);
    if (!currentLink) {
      return;
    }

    const createsFlows = updates.creates_flows ?? Boolean(currentLink.creates_flows);
    const overrideDirectionSign =
      updates.override_direction_sign !== undefined
        ? updates.override_direction_sign
        : (currentLink.metadata_override_direction_sign ?? null);

    if (
      createsFlows === Boolean(currentLink.creates_flows)
      && overrideDirectionSign === (currentLink.metadata_override_direction_sign ?? null)
    ) {
      return;
    }

    try {
      await updateCriterionLinksTrigger({
        componentId,
        links: [
          {
            id: linkId,
            creates_flows: createsFlows,
            override_direction_sign: overrideDirectionSign,
          },
        ],
      }).unwrap();
      refetchCriterionLinks();
      await onAfterSave?.();
    } catch (error: any) {
      toast.error(error?.data?.message ?? 'Failed to update criterion link settings');
    }
  };

  const onAttachCriterionLink = async (candidate: {
    metadata_id: string;
    assigned_to_other_component: boolean;
    assigned_to_current_component: boolean;
    assigned_component_id: number | null;
    assigned_component_description: string | null;
  }) => {
    const metadataId = candidate.metadata_id;
    if (componentId === null || !metadataId.trim()) {
      return;
    }

    if (candidate.assigned_to_current_component) {
      toast('That criterion link is already attached to this component');
      return;
    }

    let forceReassign = false;
    if (candidate.assigned_to_other_component && candidate.assigned_component_id != null) {
      const assignedLabel = candidate.assigned_component_description
        ? `#${candidate.assigned_component_id} (${candidate.assigned_component_description})`
        : `#${candidate.assigned_component_id}`;
      const confirmed = window.confirm(
        `This link is currently attached to component ${assignedLabel}. Reassign it to component #${componentId}?`,
      );
      if (!confirmed) {
        return;
      }
      forceReassign = true;
    }

    try {
      await attachCriterionLinkTrigger({
        componentId,
        metadataId: metadataId.trim(),
        forceReassign,
      }).unwrap();
      toast.success('Criterion link attached');
      setLinkSearchInput('');
      setLinkSearch('');
      refetchCriterionLinks();
      await onAfterSave?.();
    } catch (error: any) {
      toast.error(error?.data?.message ?? 'Failed to attach criterion link');
    }
  };

  const onDetachCriterionLink = async (linkId: number, metadataId: string) => {
    if (componentId === null) {
      return;
    }

    const confirmed = window.confirm(
      `Remove criterion link ${metadataId} from component #${componentId}?`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await detachCriterionLinkTrigger({ componentId, id: linkId }).unwrap();
      toast.success('Criterion link removed');
      refetchCriterionLinks();
      await onAfterSave?.();
    } catch (error: any) {
      toast.error(error?.data?.message ?? 'Failed to remove criterion link');
    }
  };

  return (
    <ReusableModal size="xl" isOpen={Boolean(component)} onClose={onClose}>
      <h4 className="text-center">
        {component ? `Edit Capacity #${component.id}` : 'Add New Component'}
      </h4>
      <CapacityComponentForm
        capacityComponent={component}
        onSave={onSave}
        onCancel={onClose}
      />
      {componentId !== null && (
        <div className="mt-4 border-top pt-3">
          <h5>Criterion Links</h5>
          <div className="row g-2 align-items-end mb-3">
            <div className="col-12">
              <label className="form-label">Search Criterion Links (same TSP)</label>
              <input
                type="text"
                className="form-control"
                value={linkSearchInput}
                onChange={(evt) => setLinkSearchInput(evt.target.value)}
                placeholder="Search by ticker, location name, or loc"
              />
              <div className="form-text">
                Search includes links already attached to other components. Reassigning requires confirmation.
              </div>
            </div>
          </div>

          {linkSearchInput.trim().length > 0 && (
            <div className="mb-3">
              {linkSearchInput.trim().length < 2 ? (
                <div className="text-muted">Type at least 2 characters to search.</div>
              ) : isSearchingLinks ? (
                <div className="text-muted">Searching criterion links...</div>
              ) : criterionLinkCandidates.length === 0 ? (
                <div className="text-muted">No matching criterion links found for this component's TSP.</div>
              ) : (
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>Loc</th>
                      <th>Ticker</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {criterionLinkCandidates.map((candidate) => (
                      <tr key={candidate.id}>
                        <td>{candidate.loc_name ?? '-'}</td>
                        <td>{candidate.loc ?? '-'}</td>
                        <td>{candidate.metadata_id}</td>
                        <td>
                          {candidate.assigned_to_current_component ? (
                            <span className="badge text-bg-secondary">Attached here</span>
                          ) : candidate.assigned_to_other_component && candidate.assigned_component_id != null ? (
                            <span
                              className="badge text-bg-warning text-truncate d-inline-block"
                              style={{ maxWidth: '280px' }}
                              title={
                                candidate.assigned_component_description
                                  ? `Linked to #${candidate.assigned_component_id} - ${candidate.assigned_component_description}`
                                  : `Linked to #${candidate.assigned_component_id}`
                              }
                            >
                              {candidate.assigned_component_description
                                ? `Linked to #${candidate.assigned_component_id} - ${candidate.assigned_component_description}`
                                : `Linked to #${candidate.assigned_component_id}`}
                            </span>
                          ) : (
                            <span className="badge text-bg-success">Available</span>
                          )}
                        </td>
                        <td className="text-end">
                          <button
                            type="button"
                            className={`btn btn-sm ${candidate.assigned_to_other_component ? 'btn-outline-warning' : 'btn-outline-primary'}`}
                            onClick={() => onAttachCriterionLink(candidate)}
                            disabled={isAttachingLink || candidate.assigned_to_current_component}
                          >
                            {candidate.assigned_to_current_component
                              ? 'Attached'
                              : isAttachingLink
                                ? 'Saving...'
                                : candidate.assigned_to_other_component
                                  ? 'Reassign'
                                  : 'Attach'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {isLoadingCriterionLinks ? (
            <div className="text-muted">Loading criterion links...</div>
          ) : criterionLinks.length === 0 ? (
            <div className="text-muted">No criterion links attached.</div>
          ) : (
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Location</th>
                  <th>Loc</th>
                  <th>Raw Direction Sign</th>
                  <th>Override Direction Sign</th>
                  <th>Resolved Direction</th>
                  <th>Creates Flows</th>
                  <th>Ticker</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {criterionLinks.map((link) => (
                  <tr key={link.id}>
                    <td>{link.loc_name ?? '-'}</td>
                    <td>{link.loc ?? '-'}</td>
                    <td>{link.metadata_inferred_direction_sign ?? '-'}</td>
                    <td>
                      <select
                        className="form-select form-select-sm"
                        value={
                          link.metadata_override_direction_sign == null
                            ? ''
                            : String(link.metadata_override_direction_sign)
                        }
                        disabled={isSavingLinks}
                        onChange={(evt) => {
                          const nextOverride = evt.target.value === '' ? null : Number(evt.target.value);
                          onUpdateCriterionLink(link.id, { override_direction_sign: nextOverride });
                        }}
                      >
                        <option value="">(none)</option>
                        <option value="1">1</option>
                        <option value="-1">-1</option>
                      </select>
                    </td>
                    <td>{link.resolved_direction ?? '-'}</td>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={Boolean(link.creates_flows)}
                        disabled={isSavingLinks}
                        onChange={(evt) => {
                          onUpdateCriterionLink(link.id, { creates_flows: evt.target.checked });
                        }}
                      />
                    </td>
                    <td className="text-muted small">{link.metadata_id}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => onDetachCriterionLink(link.id, link.metadata_id)}
                        disabled={isDetachingLink}
                      >
                        {isDetachingLink ? 'Removing...' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <div className="mb-2 text-muted small">
            All link changes save immediately. Reassign and remove actions require confirmation.
          </div>
        </div>
      )}
    </ReusableModal>
  );
};
